import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch } from "react-redux";
import axiosInstance from "../../../../axiosInstance";
import { closeModal } from "../../../../redux/actions/actions";

const TENANT_LABELS = {
  post_code: "Post code",
  address_1: "Address",
  city: "City",
  country: "Country (ISO code)",
  recipient_bank_sort_code: "Bank sort code",
  recipient_bank_account_number: "Bank account number",
  recipient_bank_account_name: "Bank account name",
};

const KYC_LABELS = {
  pii_front_url: "ID front",
  pii_back_url: "ID back",
  photo_url: "Selfie / photo",
};

const KYC_SLOT_BY_FIELD = {
  pii_front_url: "pii_front",
  pii_back_url: "pii_back",
  photo_url: "photo",
};

/** Payr may ask for agreement in `missing_fields.tenant`; we still send `agreement` from prefill/state but do not show it in the modal. */
const HIDDEN_TENANT_KEYS = new Set(["agreement"]);

function getAuthHeaders() {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("loginUser");
  return {
    Authorization: `Bearer ${accessToken}`,
    XRefreshToken: `${token}`,
  };
}

function isValidEmail(value) {
  const s = String(value || "").trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export default function PayrKycModal({ data }) {
  const dispatch = useDispatch();
  const { payrData, onPayrUpdate } = data || {};
  const prefill = payrData?.prefill || {};
  const missing = payrData?.missing_fields || { tenant: [], kyc: [] };

  const tenantToCollect = useMemo(() => {
    const base =
      missing.tenant && missing.tenant.length ? missing.tenant : Object.keys(TENANT_LABELS);
    return base.filter((k) => !HIDDEN_TENANT_KEYS.has(k));
  }, [missing.tenant]);
  const kycToCollect = useMemo(() => {
    const keys = (missing.kyc || []).map((k) => (k.endsWith("_url") ? k : `${k}_url`));
    return keys.length ? keys : Object.keys(KYC_LABELS);
  }, [missing.kyc]);

  const [email, setEmail] = useState(prefill.email || "");
  const [phoneNumber, setPhoneNumber] = useState(prefill.phone_number || "");
  const [dateOfBirth, setDateOfBirth] = useState(prefill.date_of_birth || "");
  const [tenant, setTenant] = useState({ ...(prefill.tenant || {}) });
  const [kyc, setKyc] = useState({ ...(prefill.kyc || {}) });

  // Lock anything that arrived already populated — the user can only fill the gaps.
  const lockedFields = useMemo(() => {
    const t = prefill.tenant || {};
    const k = prefill.kyc || {};
    return {
      email: !!String(prefill.email || "").trim(),
      phone_number: !!String(prefill.phone_number || "").trim(),
      date_of_birth: !!String(prefill.date_of_birth || "").trim(),
      tenant: Object.keys(TENANT_LABELS).reduce((acc, key) => {
        acc[key] = !!String(t[key] || "").trim();
        return acc;
      }, {}),
      kyc: Object.keys(KYC_LABELS).reduce((acc, key) => {
        acc[key] = !!String(k[key] || "").trim();
        return acc;
      }, {}),
    };
  }, [prefill]);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const setTenantField = (key, value) => setTenant((prev) => ({ ...prev, [key]: value }));

  const handleUpload = async (kycKey, file) => {
    if (!file) return;
    if (lockedFields.kyc[kycKey]) return;
    const slot = KYC_SLOT_BY_FIELD[kycKey];
    if (!slot) return;
    setUploadingSlot(slot);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("slot", slot);
      const res = await axiosInstance.post(
        `${process.env.REACT_APP_BASE_URL}payment/payr/upload`,
        formData,
        { headers: getAuthHeaders() }
      );
      const url = res?.data?.public_url;
      if (!url) {
        setError("Upload returned no URL.");
      } else {
        setKyc((prev) => ({ ...prev, [kycKey]: url }));
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Upload failed.");
    } finally {
      setUploadingSlot(null);
    }
  };

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!isValidEmail(email)) return false;
    if (!String(phoneNumber || "").trim()) return false;
    if (!String(dateOfBirth || "").trim()) return false;
    for (const key of tenantToCollect) {
      if (!String(tenant[key] || "").trim()) return false;
    }
    for (const key of kycToCollect) {
      if (!String(kyc[key] || "").trim()) return false;
    }
    return true;
  }, [submitting, email, phoneNumber, dateOfBirth, tenantToCollect, kycToCollect, tenant, kyc]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        invoice_id: payrData?.invoice_id,
        invoice_type: payrData?.invoice_type || "temp",
        email,
        phone_number: phoneNumber,
        date_of_birth: dateOfBirth,
        tenant,
        kyc,
      };
      const res = await axiosInstance.post(
        `${process.env.REACT_APP_BASE_URL}payment/payr/retry`,
        payload,
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      const next = res?.data || {};
      if (typeof onPayrUpdate === "function") onPayrUpdate(next);
      if (next.iframe_url) {
        dispatch(closeModal());
      } else if (next.requires_input) {
        // Payr still rejected — stay open with the new missing_fields surfaced via parent.
        setError("Payr still needs more details. Please review the highlighted fields.");
      } else {
        setError("Payr is still unavailable. Please try another payment method.");
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Could not complete Payr setup.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: "640px" }}>
      <Typography variant="h6" sx={{ borderBottom: "1px solid #e6e6e6", paddingBottom: ".5rem", marginBottom: "1rem" }}>
        Complete your Payr details
      </Typography>

      <Stack spacing={2}>
        <TextField
          label="Email"
          type="email"
          size="small"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          InputProps={{ readOnly: lockedFields.email }}
        />
        <TextField
          label="Phone number"
          size="small"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          fullWidth
          InputProps={{ readOnly: lockedFields.phone_number }}
        />
        <TextField
          label="Date of birth (YYYY-MM-DD)"
          size="small"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
          fullWidth
          InputProps={{ readOnly: lockedFields.date_of_birth }}
        />

        {tenantToCollect.map((key) => (
          <TextField
            key={key}
            label={TENANT_LABELS[key] || key}
            size="small"
            value={tenant[key] || ""}
            onChange={(e) => setTenantField(key, e.target.value)}
            fullWidth
            InputProps={{ readOnly: !!lockedFields.tenant[key] }}
          />
        ))}

        <Typography variant="subtitle2" sx={{ marginTop: "1rem" }}>
          KYC documents
        </Typography>

        {kycToCollect.map((key) => {
          const slot = KYC_SLOT_BY_FIELD[key];
          const uploaded = !!kyc[key];
          const isUploading = uploadingSlot === slot;
          const kycLocked = !!lockedFields.kyc[key];
          return (
            <Box
              key={key}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #e6e6e6",
                borderRadius: ".5rem",
                padding: ".5rem 1rem",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {uploaded ? (
                  <CheckCircleIcon sx={{ color: "#2e7d32", marginRight: ".5rem" }} />
                ) : null}
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{KYC_LABELS[key] || key}</Typography>
                  <Typography sx={{ fontSize: "12px", color: uploaded ? "#2e7d32" : "#666" }}>
                    {uploaded ? (kycLocked ? "On file (read-only)" : "Uploaded") : "Not uploaded"}
                  </Typography>
                </Box>
              </Box>
              <Button
                component="label"
                variant={uploaded ? "outlined" : "contained"}
                size="small"
                startIcon={isUploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
                disabled={isUploading || kycLocked}
              >
                {uploaded ? "Update" : "Upload"}
                <input
                  type="file"
                  hidden
                  accept="image/*,application/pdf"
                  onChange={(e) => handleUpload(key, e.target.files?.[0])}
                />
              </Button>
            </Box>
          );
        })}

        {error ? (
          <Typography sx={{ color: "#a94442", fontSize: "13px" }}>{error}</Typography>
        ) : null}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{ alignSelf: "flex-end", marginTop: "1rem" }}
        >
          {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit"}
        </Button>
      </Stack>
    </Box>
  );
}
